import classNames from "classnames";
import React, { FocusEventHandler, MouseEventHandler, useMemo, useState } from "react";
import { DropEvent, FileRejection, useDropzone } from "react-dropzone";
import { InputBaseProps } from "..";
import closeIcon from "../../../assets/images/close.svg";
import siteConfig from "../../../config/siteConfig";
import useTranslations from "../../../hooks/useTranslations";
import { SanityInputFileUploadProps } from "../../../models/form/SanityFormInputField";
import { isNullOrWhiteSpace } from "../../../shared/string";
import { FileWithValidationInfo, useFieldValidator } from "../Validation";
import InputContainer from "./InputContainer";
import { useFieldCustom } from "./useFieldCustom";

type OnDropFn = <T extends File>(
  acceptedFiles: T[],
  fileRejections: FileRejection[],
  event: DropEvent
) => void;

export type FileUploadProps = InputBaseProps<SanityInputFileUploadProps>;

const FileUpload = (props: FileUploadProps) => {
  const { inputContainerVariant, name, label, readonly, secure, validation } = props;

  const type = "file";

  const validationWithDefaultMaxSize = useMemo(() => {
    const maxFileSizeType = "maxFileSize";
    if (!secure && !validation?.find(v => v._type === maxFileSizeType)) {
      // Use default max file size for unsecure uploads unless the editor
      // has selected a different max file size in Sanity.
      return [
        ...(validation ?? []),
        {
          _type: maxFileSizeType,
          value: siteConfig.defaultMaxFileSize
        }
      ];
    }

    return validation;
  }, [secure, validation]);

  const translations = useTranslations();
  const validator = useFieldValidator(validationWithDefaultMaxSize);
  const [field, meta, helpers] = useFieldCustom<FileWithValidationInfo>({
    name,
    type,
    validate: validator
  });

  const onDrop: OnDropFn = (acceptedFiles, fileRejections) => {
    helpers.setTouched(true);

    if (acceptedFiles.length > 0) {
      const _file = acceptedFiles[0];
      field.onChange({
        file: _file
      });
    } else if (fileRejections.length > 0) {
      const errors = fileRejections.flatMap(rejection => rejection.errors);
      let error: string | null | undefined = null;

      if (errors.some(error => error.code === "too-many-files")) {
        error = translations.tooManyFiles;
      }

      if (!isNullOrWhiteSpace(error)) {
        field.onChange({
          customErrors: error
        });
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop,
    maxFiles: 1
  });

  const [focused, setFocused] = useState(false);

  const onFocus: FocusEventHandler<HTMLInputElement> = () => {
    setFocused(true);
  };

  const onBlur: FocusEventHandler<HTMLInputElement> = e => {
    field.onBlur(e);
    setFocused(false);
  };

  const clearInput: MouseEventHandler<HTMLButtonElement> = e => {
    if (!readonly && inputRef.current) {
      field.onChange(undefined);
      inputRef.current.value = "";
    }

    e.stopPropagation();
  };

  let dropboxClasses =
    "px-10 py-8 border text-brand-primary-20 text-lg rounded outline-none appearance-none cursor-pointer transform transition-all duration-100 break-all";

  if (focused) {
    dropboxClasses += " border-input-border-focused shadow-outline";
  }

  if (readonly || meta.value) {
    dropboxClasses += " bg-input-bg-readonly";
  }

  if (!meta.value) {
    dropboxClasses += " block border-dashed text-center";

    if (isDragActive && !readonly) {
      dropboxClasses += " bg-input-bg-dragging border-brand-primary-70";
    }
  } else {
    dropboxClasses += " flex justify-between border-transparent";
  }

  if (meta.touched && meta.error) {
    dropboxClasses += " border-brand-tertiary-50 shadow-outline-error";
  } else {
    if (isDragActive === false) {
      dropboxClasses += " border-input-border";
    }
  }

  // For secure files, make sure not to actually set the value of the dropzone field. Otherwise the file gets uploaded to Netlify.
  const inputName = secure ? `${name}_dropzone` : name;

  return (
    <InputContainer meta={meta} variant={inputContainerVariant}>
      {/* Put the label outside the border container for "sectioned" forms, ie price calculator forms */}
      {inputContainerVariant === "sectioned" && (
        <label
          className="block mb-4 text-brand-primary-20 text-lg lg:text-2xl"
          htmlFor={inputName}
          onClick={e => e.stopPropagation()}
        >
          {label}
        </label>
      )}

      <div
        {...getRootProps({
          className: "p-4 rounded border border-input-border",
          tabIndex: undefined
        })}
      >
        <input
          {...getInputProps({
            id: inputName,
            name: inputName,
            className:
              "absolute opacity-0 focus:outline-none outline-none appearance-none pointer-events-none",
            disabled: readonly,
            readOnly: readonly,
            onFocus,
            onBlur,
            tabIndex: undefined
          })}
          // Set style to an empty object to override react-dropzones display: none
          style={{}}
        />

        {inputContainerVariant === "default" && (
          <label
            className={classNames("inline-block mb-4 text-sm pointer-events-none", {
              "text-brand-primary-50": focused,
              "text-label": !focused
            })}
            htmlFor={inputName}
            onClick={e => e.stopPropagation()}
          >
            {label}
          </label>
        )}

        <span className={dropboxClasses}>
          {!meta.value ? (
            <>
              <strong>{translations.dragAndDrop}</strong> {translations.or}{" "}
              <strong>{translations.clickSelectFile}</strong>
            </>
          ) : (
            meta.value?.file?.name ?? "N/A"
          )}

          {/* Hide the remove button instead of taking it completely out of the DOM. If we remove it, then
                the e.stopPropagation does not work properly in its click event. */}
          <button
            type="button"
            onClick={clearInput}
            className={`inline-flex items-center justify-center ml-4 font-bold text-base uppercase text-brand-primary-dim cursor-pointer break-normal ${
              !meta.value ? "hidden" : ""
            }`}
          >
            {translations.remove}

            <svg
              width={12}
              height={12}
              className="fill-current transition-none flex-none ml-2"
            >
              <use xlinkHref={closeIcon.url} />
            </svg>
          </button>
        </span>
      </div>

      {secure && <input type="hidden" id={name} name={name} />}
    </InputContainer>
  );
};

export default FileUpload;
